import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo } from 'react'
import styled from 'styled-components'

import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import { ModalElementContext } from 'contexts/ModalProvider'
import BodyText from 'components/Luxkit/Typography/BodyText'
import FormatCurrency from 'components/Common/FormatCurrency'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ListItem from 'components/Luxkit/List/ListItem'
import config from 'constants/config'
import TextButton from 'components/Luxkit/Button/TextButton'
import LinePlusIcon from 'components/Luxkit/Icons/line/LinePlusIcon'
import { getSVGImageUrl } from 'lib/image/imageUtils'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { last, sum } from 'lib/array/arrayUtils'
import { fetchAvailableCredit } from 'actions/AuthActions'
import CurrencyContext from 'contexts/currencyContext'
import Heading from 'components/Luxkit/Typography/Heading'
import Caption from 'components/Luxkit/Typography/Caption'
import { INTL_DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'

import { RedeemedCard } from './common'
import CurrencyMismatchWarning from './CurrencyMismatchWarning'

const ListItemContainer = styled.div`
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.five};
`

const TotalContainer = styled.div`
  flex: 1;
`

interface Props {
  redeemedCards: Array<RedeemedCard>
  onRedeemAnother: () => void
  checkoutCurrency?: string
}

function Success({ redeemedCards, onRedeemAnother, checkoutCurrency }: Props) {
  const modalElement = useContext(ModalElementContext)

  const modalBodyRef = React.useRef<HTMLDivElement>(null)

  const currencyCode = last(redeemedCards)?.currencyCode

  const intlDateFormatter = useIntlDateFormatter()

  const dispatch = useAppDispatch()
  const creditBalance = useAppSelector((state) => state.auth.account.creditsByCurrency[currencyCode])

  const creditsLedger = useAppSelector((state) => state.auth.account.creditsLedger) ?? []

  let expiresAt: string | undefined
  creditsLedger.map(credit => {
    if (!expiresAt || credit.expiresAt > expiresAt) {
      expiresAt = credit.expiresAt
    }
  })

  const total = sum(redeemedCards.filter(card => card.currencyCode === currencyCode), card => card.amount)

  // Fetch credit balance
  useEffect(() => {
    if (currencyCode) {
      dispatch(fetchAvailableCredit(currencyCode))
    }
  }, [dispatch, currencyCode])

  useLayoutEffect(() => {
    if (modalBodyRef.current?.scrollTo) {
      // Scroll to bottom so the latest card and the "Redeem another card" button are visible
      modalBodyRef.current.scrollTo(0, modalBodyRef.current.scrollHeight)
    }
  }, [])

  const close = useCallback(() => {
    modalElement.resolve()
  }, [modalElement])

  const image = config.BRAND === 'luxuryescapes' ? getSVGImageUrl('payments-lecredit') : undefined

  const mismatchedCurrencies = useMemo(() => (
    checkoutCurrency ?
        [...new Set(
          redeemedCards
            .map(card => card.currencyCode)
            .filter(currency => currency !== checkoutCurrency),
        )] :
        []
  ), [checkoutCurrency, redeemedCards])

  return (
    <>
      <ModalHeader title="Gift credit summary" onCloseButtonClick={close} />
      <ModalBody ref={modalBodyRef}>
        <ModalContent>
          <VerticalSpacer gap={24}>
            <BodyText variant="medium">Here is a summary of redeemed {config.brandConfig.name} gift cards</BodyText>

            <VerticalSpacer gap={8}>
              <div>
                {redeemedCards.map((card, i) => {
                  const isLast = i === redeemedCards.length - 1
                  return <ListItemContainer key={card.code}>
                    <ListItem
                      title={isLast ? <BodyText variant="large" weight="bold">Gift card redeemed!</BodyText> : 'Gift card redeemed'}
                      subtitle={card.code}
                      startIcon={image && <img src={image} width="34" height="24" alt=""/>}
                      endIcon={<FormatCurrency currency={card.currencyCode} value={card.amount} format="preciseDollar" />}
                      nonInteractable
                    />
                  </ListItemContainer>
                })}
              </div>

              <TextButton kind="tertiary" onClick={onRedeemAnother} startIcon={<LinePlusIcon />}>Redeem another card</TextButton>
            </VerticalSpacer>

            {mismatchedCurrencies.length > 0 && (
              <CurrencyMismatchWarning checkoutCurrency={checkoutCurrency!} mismatchedCurrencies={mismatchedCurrencies} />
            )}
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: 'Done',
          onClick: close,
        }}
      >
        <TotalContainer>
          <CurrencyContext.Provider value={currencyCode}>
            <Heading variant="heading4" wrap="no-wrap">
              <FormatCurrency currency={currencyCode} value={total} /> <Caption variant="large" as="span">added</Caption>
            </Heading>
            {creditBalance?.loading && <Caption variant="large" colour="neutral-three">
              Fetching credit balance...
            </Caption>}
            {typeof creditBalance?.balance === 'number' && !creditBalance.loading && <Caption variant="large" colour="neutral-three">
              <FormatCurrency currency={currencyCode} value={creditBalance.balance} /> credit balance
              (Expires {intlDateFormatter(expiresAt, INTL_DMY_CASUAL_SHORT_FORMAT)})
            </Caption>}
          </CurrencyContext.Provider>
        </TotalContainer>
      </ModalFooter>
    </>
  )
}

export default Success
